import { create } from 'zustand';
import { authApi } from '@/lib/api/endpoints';
import { clearSession, readSession, writeSession } from '@/lib/auth-storage';
import { clearCompanyScope } from '@/stores/company-scope-store';
import type { Permission, Role, SessionUser } from '@/types/api';

interface AuthState {
  user: SessionUser | null;
  accessToken: string | null;
  /** Set once the stored session has been read — routes wait on this. */
  hydrated: boolean;
  login: (input: { email: string; password: string; totp?: string; remember: boolean }) => Promise<void>;
  logout: (options?: { silent?: boolean }) => Promise<void>;
  /** Called when a refresh token is rejected — drops state without an API call. */
  expire: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => {
  const stored = readSession();

  return {
    user: stored?.user ?? null,
    accessToken: stored?.accessToken ?? null,
    hydrated: true,

    login: async ({ email, password, totp, remember }) => {
      const tokens = await authApi.login({ email, password, totp });
      writeSession(tokens, remember);
      set({ user: tokens.user, accessToken: tokens.accessToken });
    },

    logout: async ({ silent = false } = {}) => {
      if (!silent && get().accessToken) {
        // A failed logout must not strand the user in a half-signed-in state.
        await authApi.logout().catch(() => undefined);
      }
      clearSession();
      // Otherwise the next person to sign in on this browser inherits whichever
      // tenant the previous platform admin had selected.
      clearCompanyScope();
      set({ user: null, accessToken: null });
    },

    expire: () => {
      clearSession();
      clearCompanyScope();
      set({ user: null, accessToken: null });
    },
  };
});

export const useCurrentUser = () => useAuthStore((s) => s.user);
export const useIsAuthenticated = () => useAuthStore((s) => s.user !== null);

/** Permission checks mirror the backend's PermissionsGuard, not the role name. */
export function useHasPermission(...required: Permission[]) {
  return useAuthStore((s) => {
    if (!s.user) return false;
    if (s.user.role === 'SUPER_ADMIN' || s.user.role === 'PLATFORM_ADMIN') return true;
    return required.every((p) => s.user!.permissions.includes(p));
  });
}

export function useHasRole(...roles: Role[]) {
  return useAuthStore((s) => (s.user ? roles.includes(s.user.role) : false));
}
